<template>
  <card-dialog
    v-model="modalVisible"
    max-width="700px"
    title="Add Selected Songs To Playlist"
  >
    <v-card-text
      class="pa-6"
    >
      <p>
        You are adding adding <strong>{{ selectedLibraryFiles.length }} selected songs</strong> to a playlist. You can
        either select a playlist that has handpicked songs, or a child playlist. Adding to an attribute based playlist
        is not possible.
      </p>

      <validation-observer ref="validation">
        <div class="mb-2 font-weight-bold">
          Select playlist
        </div>
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-autocomplete
            v-model="playlistId"
            :items="playlists.data"
            :loading="playlists.loading"
            outlined
            :error-messages="errors"
            item-text="title"
            item-value="id"
            placeholder="Select playlist"
          >
            <template v-slot:item="{ item: pl, on }">
              <v-list-item
                v-bind="$attrs"
                :disabled="!pl.is_child && !pl.is_handpicked"
                v-on="on"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    {{ pl.title }}
                  </v-list-item-title>
                  <v-list-item-subtitle :class="{ 'grey--text text--lighten-2': !pl.is_child && !pl.is_handpicked }">
                    {{ pl.client ? pl.client.name : 'All clients' }}
                    &middot;
                    <span v-if="pl.is_child">
                      Child playlist
                    </span>
                    <span v-else-if="pl.is_handpicked">
                      {{ pl.library_files_count }} handpicked songs
                    </span>
                    <span v-else>
                      Based on attributes (not possible to add to)
                    </span>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </template>
            <template v-slot:selection="{ item: pl }">
              <span>{{ pl.title }}</span>
              <span class="caption ml-2 grey--text">
                {{ pl.client ? pl.client.name : 'All clients' }}
                &middot;
                <span v-if="pl.is_child">
                  Child playlist
                </span>
                <span v-else-if="pl.attribute_values.length > 0">
                  Based on attributes (not possible to add)
                </span>
                <span v-else>
                  {{ pl.library_files_count }} handpicked songs
                </span>
              </span>
            </template>
          </v-autocomplete>
        </validation-provider>
      </validation-observer>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn
          color="primary"
          :loading="submitting"
          :disabled="!playlistId"
          @click="save()"
        >
          Add songs to playlist
        </v-btn>
      </v-card-actions>
    </template>
  </card-dialog>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog'
import Playlists from '@/services/Playlists'
import PlaylistModel from '@/models/PlaylistModel'

export default {
  name: 'AddToPlaylistDialog',
  components: { CardDialog },
  props: {
    preselectedPlaylistId: {
      type: Number,
      default: undefined
    },
    selectedLibraryFiles: {
      type: Array,
      default: () => ([])
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      playlists: {
        loading: false,
        data: []
      },
      playlistId: null,
      submitting: false,
      clients: {
        loading: false,
        data: []
      }
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    attributes() {
      return this.$store.state.attributes.data
    }
  },
  watch: {
    visible(value) {
      if (!value) return

      if (this.preselectedPlaylistId) this.playlistId = this.preselectedPlaylistId
    }
  },
  created() {
    this.fetchPlaylists()
  },
  methods: {
    async fetchPlaylists() {
      this.playlists.loading = true

      try {
        this.playlists.data = (await Playlists.fetch({
          content_type: PlaylistModel.CONTENT_TYPE_MUSIC,
          include_children: true,
          order_by: 'id',
          order: 'desc'
        }))
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'There were some errors getting playlists',
          color: 'error'
        })
        throw e
      } finally {
        this.playlists.loading = false
      }
    },
    async save() {
      if (!this.playlistId) return

      try {
        this.submitting = true

        await Playlists.addFilesToPlaylist(this.playlistId, this.selectedLibraryFiles.map(f => f.id))

        this.modalVisible = false

        this.$store.dispatch('alert', {
          text: 'Successfully added songs',
          color: 'success'
        })
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'There were some errors adding songs to playlist',
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>
